/**
 * `playKissSound` — the single chokepoint for the `/kiss` emote's audio playback, and the only
 * place in the frontend that touches an `Audio` element. Keeping it single means a future mute
 * control has exactly one place to gate rather than a call-site hunt. **Such a gate belongs at
 * the top of this function, before the rate-cap check** — a muted call shouldn't play NOR
 * consume the rate limiter's window, so once unmuted the cap is judged against the last REAL play.
 *
 * The asset is a plain public static file (`frontend/public/audio/kiss.mp3`, root-relative like
 * `/frames/<id>.png` elsewhere here) — Vite serves `public/` at the origin root in dev, copies it
 * into `dist/` verbatim on build, and the e2e harness + production reverse proxy both serve
 * `dist/` from that same origin, so one root-relative path works in all three.
 *
 * **Rate cap.** A fresh `HTMLAudioElement` per call, but at most one play per
 * {@link RATE_CAP_MS}; the excess is **dropped, not queued** — queueing a 20-kiss burst would
 * replay it as a wall of sequential chimes, worse than a noisy-but-brief pile-up. The decision is
 * extracted as the pure {@link shouldPlayKiss} predicate so it's unit-testable without a DOM:
 * this repo has no jsdom/headless renderer to drive `Audio`/timing through a real mount.
 *
 * **Autoplay is not unlocked here, deliberately.** A gesture-free tab's `play()` rejects with
 * `NotAllowedError`, and that case is reachable (a reload with a remembered `localStorage` token
 * auto-reclaims the session with zero gesture — `useGameSession.ts`'s mount effect). But Chrome's
 * autoplay gate keys on **sticky** (permanent, per-document) user activation, not a transient
 * per-call-stack one: the player's next click/tap/key ANYWHERE on the page permanently unlocks
 * every future `play()` for the rest of that document's life, with no code needed. An explicit
 * "unlock on first gesture" listener would be a no-op — it can't shrink the reload-to-first-click
 * gap, and recovering the kiss(es) missed inside it would need a replay queue. So the one
 * rejection this swallows is exactly that narrow, self-healing, one-time gap.
 *
 * **There is no per-sound mute.** This sound is a different domain from `sfxEngine`'s "Mute SFX"
 * (synthesized UI cues, not a sampled social sound), so that toggle does not cover it — `/kiss`
 * always plays, subject only to the rate cap below.
 */
const KISS_SOUND_URL = "/audio/kiss.mp3";

/**
 * The minimum spacing, in ms, between two plays. The asset itself is ~1.02s long, so at 400ms a
 * genuinely later kiss still reads as a separate moment from whichever one is still audibly
 * playing, while a spam burst (repeated `/kiss` tens of ms apart — a held-down Enter key, a
 * pasted block of commands) collapses to its first hit rather than piling up.
 */
const RATE_CAP_MS = 400;

/** The wall-clock timestamp (ms, `Date.now()`) of the last play that actually fired. */
let lastPlayedAt = 0;

/**
 * The pure "should this play fire?" decision, extracted so it's testable without a DOM or a
 * mocked `Audio`/timer. `capMs` defaults to the real policy's {@link RATE_CAP_MS} but is an
 * explicit parameter so tests can pin the boundary without depending on the module constant.
 */
export function shouldPlayKiss(now: number, lastPlayedAtMs: number, capMs: number = RATE_CAP_MS): boolean {
  return now - lastPlayedAtMs >= capMs;
}

export function playKissSound(): void {
  const now = Date.now();
  if (!shouldPlayKiss(now, lastPlayedAt)) return; // dropped — a spam burst, not a new moment
  lastPlayedAt = now;
  const audio = new Audio(KISS_SOUND_URL);
  void audio.play().catch(() => {
    // Swallowed deliberately — see the file header. This is Chrome's autoplay policy rejecting
    // playback on a tab that hasn't yet had ANY user gesture since its last load; it self-heals
    // on the player's next click/tap/key anywhere on the page.
  });
}
